import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { deleteLayout, editLayout } from "store/thunk/layout";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { isReportPageRendering } from "utils/reportUtils";
import { capitalizeFirstLetter, customDebounce } from "utils/utils";
import { INPUT_MAXIMUM, INPUT_MINIMUM } from "constants/globalConstants";
import { SYSTEMS_PAGES } from "constants/pages.constant";

import AutogenerateForm from "pages/Report/AutogenerateForm";
import CustomModal from "components/CustomModal";
import DupontDialog from "components/DupontDialog";
import InputBox from "components/InputBox";

import { DELETE_DIALOG_STRINGS, LAYOUT_STRINGS, valueStrings, WidgetStrings } from "../../constants/constantStrings";

import "./LayoutEdit.css";

function LayoutEdit(props) {
  const { admin, layouts, currentLayout, page } = props;
  const { Home, Name, AutoReport } = currentLayout;
  const [editError, setError] = useState("");
  const [deleteDashboard, setDeleteDashboard] = useState(false);
  const [layoutData, setLayoutData] = useState({ Home, Name, AutoReport });
  const [isFormInvalid, setIsFormInvalid] = useState(true);
  const [defaultDashboardFlag] = useState(currentLayout.DefaultDashboard);
  const pageName = capitalizeFirstLetter(props.page.slice(0, -1));
  const objAppInsights = useAppInsightsHandler();

  const closeModal = () => props.setIsEditLayoutFormOpen(false);

  const nameChangeHandler = Name => {
    const data = { ...layoutData, Name };
    setLayoutData(data);
    debounceFn(data);
  };

  const isUniqueLayoutName = data => {
    const { ID, UserID } = currentLayout;
    const isNotUnique = layouts.some(layout => layout.ID !== ID && layout.Name === data.Name?.trim() && layout.UserID === UserID);
    const titleLength = data.Name.length < INPUT_MINIMUM || data.Name.length > INPUT_MAXIMUM ? LAYOUT_STRINGS.titleLength : "";
    const errrMsg = titleLength ? titleLength : isNotUnique ? LAYOUT_STRINGS.nonUniqueLayoutName : "";
    setError(errrMsg);
    setIsFormInvalid(!data.Name || data.Name.trim() === "" || isNotUnique || !isDataChanged(data) || titleLength);
  };
  const debounceFn = useCallback(customDebounce(isUniqueLayoutName), []);

  const setPrimaryLayoutHandler = () => {
    if (!currentLayout.Home) {
      const Home = !layoutData.Home;
      const data = { ...layoutData, Home };
      validateForm(data);
    }
  };

  const validateForm = data => {
    const { Name } = data;
    const isInvalid = !Name || !isDataChanged(data);
    setIsFormInvalid(isInvalid);
    setLayoutData(data);
  };

  const isDataChanged = data => Name !== data.Name || Home !== data.Home || AutoReport !== data.AutoReport;

  const saveBtnHandler = () => {
    objAppInsights.trackEvent("editLayoutFormSubmitted");
    const scheduling = null;
    props.onEditLayout(page, currentLayout, layoutData.Name?.trim(), layoutData.Home, scheduling, layoutData.AutoReport);
    props.setIsEditLayoutFormOpen(false);
  };

  const handleDeleteDashboard = () => setDeleteDashboard(true);
  const onDeleteDlgClose = action => {
    setDeleteDashboard(false);
    if (action === "CONFIRM") {
      deleteLayout();
    }
  };

  const onAutoReportToggle = () => {
    const AutoReport = !layoutData.AutoReport;
    const formData = { ...layoutData, AutoReport };
    validateForm(formData);
  };

  const deleteLayout = () => {
    const userID = admin && currentLayout?.UserID !== layouts[0]?.UserID ? currentLayout.UserID : "";
    props.onDeleteLayout(page, currentLayout.ID, admin, userID, layouts);
    props.setIsEditLayoutFormOpen(false);
  };

  const getPrimaryLayoutTxt = () => {
    const txt = currentLayout.Home ? LAYOUT_STRINGS.primaryLayout : LAYOUT_STRINGS.setAsPrimary;
    return txt?.replace("[%]", pageName);
  };

  const getPrimaryLayoutClass = () => (currentLayout.Home ? "not-allowed" : "pointer");

  return (
    <>
      <CustomModal
        show={true}
        title={`${LAYOUT_STRINGS.editLayoutModalTitle.replace("[%]", pageName)}`}
        confirmBtnText={valueStrings.save}
        description={`${LAYOUT_STRINGS.editLayoutDesc.replace("[%]", pageName)}${
          defaultDashboardFlag ? ". " + LAYOUT_STRINGS.defaultDashboardDesc : ""
        }`}
        secondaryDescription={`${valueStrings.mandatoryField}`}
        close={closeModal}
        confirmBtnAction={saveBtnHandler}
        confirmDisabled={isFormInvalid}
        isBackBtnHidden={isReportPageRendering}
      >
        <>
          <div className="titleInputBox">
            <InputBox
              labelText={`${LAYOUT_STRINGS.layoutTitle.replace("[%]", pageName)}*`}
              name="layoutName"
              value={layoutData.Name}
              placeholder={WidgetStrings.titlePlaceholder}
              maxLength={INPUT_MAXIMUM}
              onChange={nameChangeHandler}
              errorMsg={editError}
            />
          </div>
          {props.page === SYSTEMS_PAGES.REPORTS && (
            <div className="AddLayoutContainerPadding">
              <AutogenerateForm isAutoGenerateEnabled={layoutData.AutoReport} setIsAutoGenerateEnabled={onAutoReportToggle} />
            </div>
          )}
          <div className="edit-options-container" id="editOptions">
            <div
              className={`${getPrimaryLayoutClass()} ${layoutData.Home ? "isHomeIcon" : "setHomeIcon"} iconAlignment`}
              onClick={setPrimaryLayoutHandler}
            >
              <span className="edit-option-text"> {getPrimaryLayoutTxt()} </span>
            </div>
            {!defaultDashboardFlag && <hr className="divider" />}
            {!defaultDashboardFlag && (
              <div className="deleteIcon iconAlignment" onClick={handleDeleteDashboard}>
                <span className="edit-option-text"> {LAYOUT_STRINGS.deleteLayout.replace("[%]", pageName)} </span>
              </div>
            )}
          </div>
        </>
      </CustomModal>
      {deleteDashboard && (
        <DupontDialog
          open={deleteDashboard}
          type="primary"
          title={`${DELETE_DIALOG_STRINGS.delete.replace("[%]", pageName.toLowerCase())} ${props.currentLayout.Name}?`}
          cancelBtnText={DELETE_DIALOG_STRINGS.actionNo}
          confirmBtnText={DELETE_DIALOG_STRINGS.actionYes}
          onClose={onDeleteDlgClose}
        >
          {DELETE_DIALOG_STRINGS.action}
        </DupontDialog>
      )}
    </>
  );
}

const mapStateToProps = ({ auth, layout }, ownProps) => ({
  idToken: auth.idToken,
  layouts: layout.layouts[ownProps.systemID][ownProps.page]
});

const mapDispatchToProps = dispatch => ({
  onEditLayout: (page, layout, name, isHome, scheduling, autoReport) => dispatch(editLayout(page, layout, name, isHome, scheduling, autoReport)),
  onDeleteLayout: (page, layout, admin, userID, layouts) => dispatch(deleteLayout(page, layout, admin, userID, layouts))
});

export default connect(mapStateToProps, mapDispatchToProps)(LayoutEdit);
